import { ActionIcon, Group, Loader, Menu, Modal, Textarea, Tooltip } from '@mantine/core';
import { useDisclosure } from '@mantine/hooks';
import { IconMoodSmile, IconPaperclip, IconPhoto, IconSend, IconUpload } from '@tabler/icons-react';
import { Suspense, lazy, useRef, useState } from 'react';
import { QuickRepliesPopover } from '../QuickRepliesPopover';
import { MediaLibraryPicker, type PickedMedia } from './MediaLibraryPicker';
import type { ChannelCapabilities } from './types';

// emoji-picker-react ships ~200KB gzipped — keep it lazy.
const EmojiPicker = lazy(() => import('emoji-picker-react'));

/** The message composer — textarea + send, plus capability-gated attach,
 *  quick-replies, emoji, and a channel-specific `extraButtons` slot. */
export function Composer({
  capabilities,
  selectionId,
  draft,
  onDraftChange,
  onSend,
  onPickFile,
  onSendFromLibrary,
  onSendTemplate,
  sending,
  sendingMedia,
  sendingFromLibrary,
  disabled,
}: {
  capabilities: ChannelCapabilities;
  selectionId: number;
  draft: string;
  onDraftChange: (v: string) => void;
  onSend: () => void;
  onPickFile?: (file: File | null) => void;
  onSendFromLibrary?: (item: PickedMedia) => void;
  onSendTemplate?: (tpl: { kind: string; payload: unknown }) => void;
  sending: boolean;
  sendingMedia?: boolean;
  sendingFromLibrary?: boolean;
  disabled: boolean;
}) {
  const [emojiOpen, setEmojiOpen] = useState(false);
  const [libOpen, libCtl] = useDisclosure(false);
  const fileInputRef = useRef<HTMLInputElement>(null);
  const insertText = (t: string) => onDraftChange(draft ? `${draft} ${t}` : t);
  const sendTemplate = (tpl: { kind: string; payload: unknown }) => onSendTemplate?.(tpl);
  const sendMedia = onPickFile ? (f: File) => onPickFile(f) : undefined;
  const { composer } = capabilities;

  return (
    <Group p="sm" wrap="nowrap" gap="xs" align="flex-end">
      {composer.attachments && (onPickFile || onSendFromLibrary) && (
        <>
          <input
            type="file"
            ref={fileInputRef}
            style={{ display: 'none' }}
            accept="image/png,image/jpeg,image/gif,video/mp4,audio/mpeg,audio/mp4,audio/aac,application/pdf"
            onChange={(e) => {
              const f = e.target.files?.[0] ?? null;
              if (f) onPickFile?.(f);
              e.target.value = '';
            }}
          />
          <Menu position="top-start" withinPortal shadow="md">
            <Menu.Target>
              <Tooltip label="Attach">
                <ActionIcon
                  size="lg"
                  variant="subtle"
                  color="gray"
                  disabled={disabled}
                  loading={sendingMedia || sendingFromLibrary}
                >
                  <IconPaperclip size={18} />
                </ActionIcon>
              </Tooltip>
            </Menu.Target>
            <Menu.Dropdown>
              {onPickFile && (
                <Menu.Item
                  leftSection={<IconUpload size={16} />}
                  onClick={() => fileInputRef.current?.click()}
                >
                  Upload from device
                </Menu.Item>
              )}
              {onSendFromLibrary && (
                <Menu.Item leftSection={<IconPhoto size={16} />} onClick={libCtl.open}>
                  Choose from library
                </Menu.Item>
              )}
            </Menu.Dropdown>
          </Menu>
        </>
      )}
      {composer.quickReplies && (
        <QuickRepliesPopover channel={capabilities.channel} onPick={insertText} />
      )}
      {composer.extraButtons?.({ selectionId, insertText, disabled, sendTemplate, sendMedia })}
      {composer.emoji && (
        <Tooltip label="Emoji">
          <ActionIcon size="lg" variant="subtle" color="gray" onClick={() => setEmojiOpen(true)}>
            <IconMoodSmile size={18} />
          </ActionIcon>
        </Tooltip>
      )}
      <Textarea
        flex={1}
        autosize
        minRows={1}
        maxRows={6}
        placeholder={composer.placeholder}
        value={draft}
        onChange={(e) => onDraftChange(e.currentTarget.value)}
        onKeyDown={(e) => {
          if (e.key === 'Enter' && !e.shiftKey) {
            e.preventDefault();
            onSend();
          }
        }}
      />
      <Tooltip label="Send (Enter)">
        <ActionIcon
          size="lg"
          variant="filled"
          color={capabilities.accent}
          disabled={!draft.trim() || disabled}
          loading={sending}
          onClick={onSend}
        >
          <IconSend size={18} />
        </ActionIcon>
      </Tooltip>

      {composer.emoji && (
        <Modal
          opened={emojiOpen}
          onClose={() => setEmojiOpen(false)}
          withCloseButton={false}
          padding={0}
          size="auto"
          centered
          zIndex={1000}
        >
          <Suspense fallback={<Loader m="xl" />}>
            <EmojiPicker
              onEmojiClick={(e) => {
                onDraftChange(draft + e.emoji);
                setEmojiOpen(false);
              }}
            />
          </Suspense>
        </Modal>
      )}

      {onSendFromLibrary && (
        <MediaLibraryPicker
          opened={libOpen}
          onClose={libCtl.close}
          busy={sendingFromLibrary}
          onSelect={(item) => {
            onSendFromLibrary(item);
            libCtl.close();
          }}
        />
      )}
    </Group>
  );
}
